import { useCallback, useEffect, useState } from "react";

/**
 * Ink-shop cart — localStorage-backed so it survives reloads and
 * doesn't require the customer to have an account.
 *
 * Shape stored under `last.ink_cart.v1`:
 *   [
 *     { product_id, variant_id, quantity, added_at }
 *   ]
 *
 * A single "cart-updated" browser event lets multiple mounted
 * components (header badge + /cart page + product page) react to
 * each other's mutations without a global state library.
 */

const STORAGE_KEY = "last.ink_cart.v1";
const EVENT_NAME = "last-ink-cart-updated";

function readStorage() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive shape check — drop entries that don't match.
    return parsed.filter(
      (it) => it && typeof it === "object" && it.product_id && it.variant_id
    );
  } catch (_e) {
    return [];
  }
}

function writeStorage(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (_e) {
    // Swallow — private-mode Safari etc. Cart will be memory-only that
    // session, but adds/removes still work.
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function useCart() {
  const [items, setItems] = useState(() =>
    typeof window === "undefined" ? [] : readStorage(),
  );

  // Subscribe to changes triggered by any mounted useCart instance
  // (cross-component sync) + the browser `storage` event (cross-tab).
  useEffect(() => {
    const refresh = () => setItems(readStorage());
    window.addEventListener(EVENT_NAME, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(EVENT_NAME, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const add = useCallback((product_id, variant_id, quantity = 1) => {
    const cur = readStorage();
    const idx = cur.findIndex(
      (it) => it.product_id === product_id && it.variant_id === variant_id,
    );
    if (idx >= 0) {
      cur[idx] = { ...cur[idx], quantity: cur[idx].quantity + quantity };
    } else {
      cur.push({
        product_id,
        variant_id,
        quantity,
        added_at: new Date().toISOString(),
      });
    }
    writeStorage(cur);
  }, []);

  const remove = useCallback((product_id, variant_id) => {
    const next = readStorage().filter(
      (it) => !(it.product_id === product_id && it.variant_id === variant_id),
    );
    writeStorage(next);
  }, []);

  const updateQty = useCallback((product_id, variant_id, quantity) => {
    const clamped = Math.max(0, Math.min(50, Number(quantity) || 0));
    if (clamped === 0) {
      remove(product_id, variant_id);
      return;
    }
    const cur = readStorage();
    const idx = cur.findIndex(
      (it) => it.product_id === product_id && it.variant_id === variant_id,
    );
    if (idx >= 0) {
      cur[idx] = { ...cur[idx], quantity: clamped };
      writeStorage(cur);
    }
  }, [remove]);

  const clear = useCallback(() => {
    writeStorage([]);
  }, []);

  const count = items.reduce((n, it) => n + (it.quantity || 0), 0);

  return { items, add, remove, updateQty, clear, count };
}
